import { useState } from "react";
import { newIdempotencyKey } from "./client";
import { isApiProblem } from "./problem";

/**
 * Idempotency-Key discipline for one user action (SA-01 §5 conventions), e.g. clicking Create, Upload or New policy.
 *
 * - The key is generated when the action starts and reused for every retry of the same submission, automatic or by the
 *   user, so the server replays the original instead of creating twice.
 * - A new key only after a response the server stores: a 2xx (the action is done) or a stored refusal, 400, 403, 404,
 *   409 other than in progress, 413 or 422.
 * - The same key after a response the server does not store, so the retry runs again safely: a network failure, 401 (for
 *   example after signing in again), 429 (after waiting), any 5xx, and the in-progress 409.
 * - 409 with `Retry-After` means the original is still in progress: wait the given seconds (clamped to 1-5 s) and retry
 *   with the same key, up to 3 times, then throw; `problemMessage` says it is still being saved.
 * - A different payload (when passed to `run`) is a different submission and gets a new key, since the server refuses
 *   the same key with a different body (422).
 *
 * Only for operations whose contract declares `Idempotency-Key`: `send` sets the header itself, so the generated types
 * refuse it anywhere else. Never for `POST /users/{userId}/password-reset`.
 */
export const IN_PROGRESS_RETRIES = 3;
export const RETRY_AFTER_MIN_SECONDS = 1;
export const RETRY_AFTER_MAX_SECONDS = 5;
/** Refusals the server stores and replays for the same key (SA-01 §5 conventions); the in-progress 409 is handled first. */
export const STORED_REFUSALS: ReadonlySet<number> = new Set([400, 403, 404, 409, 413, 422]);

export interface IdempotentAction {
  /** The key the next submission sends. */
  readonly key: string;
  /** Sends one submission with the action's key, applying the rules above. */
  run<T>(send: (idempotencyKey: string) => Promise<T>, payload?: unknown): Promise<T>;
}

export interface IdempotentActionOptions {
  /** Tests only. */
  newKey?: () => string;
  /** Tests only: replaces the wait between in-progress retries. */
  sleep?: (ms: number) => Promise<void>;
}

const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/** The wait before retrying an in-progress 409, from its Retry-After seconds, clamped to 1-5 s. */
export function inProgressDelayMs(retryAfterSeconds: number | undefined): number {
  const seconds = retryAfterSeconds !== undefined && Number.isFinite(retryAfterSeconds) ? retryAfterSeconds : RETRY_AFTER_MIN_SECONDS;
  return Math.min(RETRY_AFTER_MAX_SECONDS, Math.max(RETRY_AFTER_MIN_SECONDS, seconds)) * 1000;
}

export function createIdempotentAction(options: IdempotentActionOptions = {}): IdempotentAction {
  const newKey = options.newKey ?? newIdempotencyKey;
  const sleep = options.sleep ?? wait;
  let key = newKey();
  let sentPayload: string | undefined;

  function renew() {
    key = newKey();
    sentPayload = undefined;
  }

  return {
    get key() {
      return key;
    },
    async run<T>(send: (idempotencyKey: string) => Promise<T>, payload?: unknown): Promise<T> {
      if (payload !== undefined) {
        const fingerprint = JSON.stringify(payload);
        if (sentPayload !== undefined && sentPayload !== fingerprint) renew();
        sentPayload = fingerprint;
      }
      const attemptKey = key;
      for (let retries = 0; ; retries += 1) {
        try {
          const result = await send(attemptKey);
          renew();
          return result;
        } catch (error) {
          if (isApiProblem(error) && error.isInProgress) {
            if (retries < IN_PROGRESS_RETRIES) {
              await sleep(inProgressDelayMs(error.retryAfterSeconds));
              continue;
            }
            throw error;
          }
          if (isApiProblem(error) && STORED_REFUSALS.has(error.status)) renew();
          throw error;
        }
      }
    },
  };
}

/** One IdempotentAction for the lifetime of a form or dialog. */
export function useIdempotentAction(): IdempotentAction {
  const [action] = useState(() => createIdempotentAction());
  return action;
}
